/**
 * Button component
 * @author Liwei Yeh <liwei.yeh@adasasistemas.com>
 */

// Library
import React from 'react';
// Spinner
import { PulseLoader } from 'react-spinners';

// CSS Style for this module
import styles from './button.module.scss';

/**
 * Interface for Button props
 */
interface ButtonProps {
	/**
	 * Custom styles of the button
	 */
	customStyles?: string;
	/**
	 * preset type which has its own styling and functionality
	 */
	type?: 'primary' | 'secondary' | 'default' | 'disabled';
	/**
	 * preset size of the button
	 */
	size?: 'small' | 'large' | 'giant' | 'auto';
	/**
	 * aria role (accessibility)
	 * see : https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Roles
	 */
	role?: string;
	/**
	 * tabulation index for accessibility
	 */
	tabIndex?: number;
	/**
	 * Set button focused by default
	 */
	autoFocus?: boolean;
	/**
	 * Button contents
	 */
	children: React.ReactNode;
	/**
	 * Optional click handler
	 */
	onClick?: () => void;
	/**
	 * If true, replace button text by a spinner and defuse interaction
	 */
	isLoading?: boolean;
	/**
	 * If true, disable all interactions for this component
	 */
	disabled?: boolean;
	/**
	 * aria-label
	 */
	ariaLabel?: string;
}

/**
 * An styled general button
 * @param {ButtonProps} Prop
 * @returns  a button
 */
export const Button = ({
	size,
	type = 'default',
	customStyles,
	isLoading = false,
	disabled = type == 'disabled',
	children,
	ariaLabel = 'button',
	tabIndex,
	autoFocus,
	onClick,
	...props
}: ButtonProps) => {
	type = disabled ? 'disabled' : type;
	return (
		<button
			aria-label={ariaLabel}
			tabIndex={tabIndex}
			autoFocus={autoFocus}
			type="button"
			className={[
				styles[`button-${type}`],
				size && styles[`button-${size}`],
				styles[`button--activable-${type}--loading-${isLoading}`],
				customStyles,
			].join(' ')}
			disabled={disabled}
			onClick={
				type === 'disabled' || isLoading
					? () => {
							return;
						}
					: onClick
			}
			{...props}
		>
			{!isLoading ? (
				children
			) : (
				<div style={{ position: 'relative' }}>
					<div style={{ visibility: 'hidden' }}>{children}</div>
					<PulseLoader className={[styles.spinner, styles[`spinner-${type}`]].join(' ')} />
				</div>
			)}
		</button>
	);
};
